import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, query, where, onSnapshot, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { requestExport } from '../services/exportService';
import { submitCaseToEngine } from '../services/engineService';
import { isAdmin } from '../config/admins';

export default function Dashboard({ user }) {
  const [cases, setCases]           = useState([]);
  const [exporting, setExporting]   = useState(null); // caseId being exported
  const [deleting, setDeleting]     = useState(null); // caseId being deleted
  const [sending, setSending]       = useState(null); // caseId being sent to engine
  const [menuOpen, setMenuOpen]     = useState(null); // caseId whose ⋯ menu is open
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(collection(db, 'cases'), where('practiceId', '==', user.uid));
    const unsub = onSnapshot(q, snap => {
      const allCases = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      allCases.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return bTime - aTime;
      });
      setCases(allCases);
    }, err => console.error('Firestore query error:', err));
    return unsub;
  }, [user]);

  const total     = cases.length;
  const inReview  = cases.filter(c => c.status === 'review').length;
  const completed = cases.filter(c => c.status === 'complete').length;

  const handleSignOut = () => signOut(auth);

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleDownload = async (caseData) => {
    setExporting(caseData.id);
    try {
      const url = await requestExport(caseData, user);
      // Trigger browser download
      const a = document.createElement('a');
      a.href = url;
      a.download = `${caseData.caseNumber}_provisional.stl`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      alert(err.message);
    } finally {
      setExporting(null);
    }
  };

  const handleDelete = async (caseData) => {
    const ok = window.confirm(
      `Delete case ${caseData.caseNumber} — ${caseData.patientLast}, ${caseData.patientFirst}?\n\n` +
      `This permanently removes the case and all of its uploaded files. This cannot be undone.`
    );
    if (!ok) return;

    setDeleting(caseData.id);
    try {
      // 1. Delete all uploaded files from Storage
      const files = caseData.files || [];
      await Promise.all(files.map(async (f) => {
        if (!f?.path) return;
        try {
          await deleteObject(ref(storage, f.path));
        } catch (e) {
          // Ignore files that are already gone; surface anything else
          if (e.code !== 'storage/object-not-found') throw e;
        }
      }));

      // 2. Delete the case record (the live listener removes the row automatically)
      await deleteDoc(doc(db, 'cases', caseData.id));
    } catch (err) {
      alert(`Could not delete case: ${err.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const handleSendToEngine = async (caseData) => {
    if (sending) return;
    const files = caseData.files || [];
    if (files.length === 0) {
      alert('This case has no uploaded files to send. Add files first (⋯ → Add / edit files).');
      return;
    }
    setSending(caseData.id);
    try {
      const resp = await submitCaseToEngine(caseData.caseNumber, caseData.practiceId || user.uid, files, caseData.toothLibrary);
      await updateDoc(doc(db, 'cases', caseData.id), {
        engineJobId: resp.job_id,
        engineStatus: 'processing',
        status: 'processing',
      });
    } catch (err) {
      await updateDoc(doc(db, 'cases', caseData.id), { engineStatus: 'queued' });
      alert(`Couldn't reach the engine: ${err.message}\n\nThe case is marked "queued" — try again in a moment.`);
    } finally {
      setSending(null);
    }
  };

  // ── Status badge ──────────────────────────────────────────────────────────
  const statusConfig = {
    draft:       { label: 'Draft',          color: '#aaa',    bg: '#f5f5f5' },
    uploaded:    { label: 'Uploaded',        color: '#3b82f6', bg: '#eff6ff' },
    processing:  { label: 'AI Processing',   color: '#c8922a', bg: '#fffbf0' },
    review:      { label: 'In Review',       color: '#8b5cf6', bg: '#f5f3ff' },
    complete:    { label: 'Complete',        color: '#16a34a', bg: '#f0fdf4' },
    failed:      { label: 'Failed',          color: '#dc2626', bg: '#fef2f2' },
  };

  const getStatusConfig = (status) =>
    statusConfig[status] || statusConfig.draft;

  return (
    <>
      <nav className="navbar">
        <div className="nav-logo">
          <img src="/logo.png" alt="ARCHX AI" style={{ height: '100px', width: 'auto' }} />
        </div>
        <div className="nav-right">
          {isAdmin(user) && (
            <button
              className="nav-signout"
              onClick={() => navigate('/engine')}
              style={{ background: '#1e293b', color: '#fbbf24', borderColor: '#1e293b' }}
            >
              ⚡ Engine Monitor
            </button>
          )}
          <span className="nav-user">{user.email}</span>
          <button className="nav-signout" onClick={handleSignOut}>Sign out</button>
        </div>
      </nav>

      <div className="page">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{total}</div>
            <div className="stat-label">TOTAL CASES</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{inReview}</div>
            <div className="stat-label">IN REVIEW</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{completed}</div>
            <div className="stat-label">COMPLETED</div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Your cases</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn-gold" onClick={() => navigate('/inbox')} style={{ background: '#1e293b', color: '#fff' }}>📥 Incoming files</button>
              <button className="btn-gold" onClick={() => navigate('/cases/new')}>+ New case</button>
            </div>
          </div>

          {cases.length === 0 ? (
            <div className="empty-state">
              No cases yet — click "New case" to submit your first one.
            </div>
          ) : (
            cases.map(c => {
              const sc        = getStatusConfig(c.status);
              const isReady    = c.status === 'complete';
              const isProc     = c.status === 'processing';
              const isLoading  = exporting === c.id;
              const isDeleting = deleting === c.id;
              const isSending  = sending === c.id;
              const canSend    = !isProc && !isReady; // not already processing or complete

              return (
                <div className="case-row" key={c.id}
                  style={{ alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>

                  {/* Case info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="case-name">
                      {c.patientLast}, {c.patientFirst} — {c.caseNumber}
                    </div>
                    <div className="case-meta">
                      {formatDate(c.createdAt)} · {c.caseType || c.archType || '—'} · {c.fileCount || 0} file{c.fileCount !== 1 ? 's' : ''}
                      {c.engineStatus === 'processing' && (
                        <span style={{ color: '#c8922a', marginLeft: '8px' }}>
                          · AI processing...
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Status badge */}
                  <span style={{
                    padding: '4px 12px', borderRadius: '20px',
                    fontSize: '12px', fontWeight: '600',
                    color: sc.color, background: sc.bg,
                    whiteSpace: 'nowrap',
                    display: 'flex', alignItems: 'center', gap: '6px',
                  }}>
                    {isProc && (
                      <span style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        background: '#c8922a',
                        display: 'inline-block',
                        animation: 'pulse 1.5s infinite',
                      }} />
                    )}
                    {sc.label}
                  </span>

                  {/* Send to engine — for cases not yet processing/complete */}
                  {canSend && (
                    <button
                      onClick={() => handleSendToEngine(c)}
                      disabled={isSending}
                      title="Send this case to the AI engine for processing"
                      style={{
                        padding: '7px 16px', borderRadius: '8px', border: 'none',
                        background: isSending ? '#e5e7eb' : '#1e293b',
                        color: isSending ? '#aaa' : '#fbbf24',
                        fontWeight: '700', fontSize: '13px',
                        cursor: isSending ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap', transition: 'all 0.2s',
                      }}
                    >
                      {isSending ? 'Sending…' : '▶ Send to engine'}
                    </button>
                  )}

                  {/* Download button — only for complete cases */}
                  {isReady && (
                    <button
                      onClick={() => handleDownload(c)}
                      disabled={isLoading}
                      style={{
                        padding: '7px 18px', borderRadius: '8px', border: 'none',
                        background: isLoading ? '#e5e7eb' : '#c8922a',
                        color: isLoading ? '#aaa' : 'white',
                        fontWeight: '600', fontSize: '13px',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap', transition: 'all 0.2s',
                      }}
                    >
                      {isLoading ? 'Preparing...' : '↓ Download STL'}
                    </button>
                  )}

                  {/* Actions menu — compact ⋯ with a dropdown */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setMenuOpen(menuOpen === c.id ? null : c.id)}
                      disabled={isDeleting}
                      title="More actions"
                      style={{
                        width: '32px', height: '32px', borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        background: isDeleting ? '#f5f5f5' : 'white',
                        color: '#666', fontSize: '20px', lineHeight: 1,
                        cursor: isDeleting ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s',
                      }}
                    >
                      {isDeleting ? '…' : '⋯'}
                    </button>

                    {menuOpen === c.id && (
                      <>
                        {/* click-away layer */}
                        <div
                          onClick={() => setMenuOpen(null)}
                          style={{ position: 'fixed', inset: 0, zIndex: 50 }}
                        />
                        <div style={{
                          position: 'absolute', right: 0, top: '38px', zIndex: 51,
                          background: 'white', border: '1px solid #eee',
                          borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          overflow: 'hidden', minWidth: '170px',
                        }}>
                          <button
                            onClick={() => { setMenuOpen(null); handleSendToEngine(c); }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                            style={{
                              width: '100%', textAlign: 'left', padding: '10px 14px',
                              border: 'none', borderBottom: '1px solid #f1f1f1', background: 'white', color: '#1e293b',
                              fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '8px',
                            }}
                          >
                            ▶ Send to engine
                          </button>
                          <button
                            onClick={() => { setMenuOpen(null); navigate(`/cases/${c.id}/viewer`); }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                            style={{
                              width: '100%', textAlign: 'left', padding: '10px 14px',
                              border: 'none', borderBottom: '1px solid #f1f1f1', background: 'white', color: '#1e293b',
                              fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '8px',
                            }}
                          >
                            🧊 3D View
                          </button>
                          <button
                            onClick={() => { setMenuOpen(null); navigate(`/cases/${c.id}/edit`); }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                            style={{
                              width: '100%', textAlign: 'left', padding: '10px 14px',
                              border: 'none', borderBottom: '1px solid #f1f1f1', background: 'white', color: '#1e293b',
                              fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '8px',
                            }}
                          >
                            ✏️ Add / edit files
                          </button>
                          <button
                            onClick={() => { setMenuOpen(null); handleDelete(c); }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#fef2f2')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                            style={{
                              width: '100%', textAlign: 'left', padding: '10px 14px',
                              border: 'none', background: 'white', color: '#dc2626',
                              fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '8px',
                            }}
                          >
                            🗑 Delete case
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Beta banner */}
        <div style={{
          marginTop: '24px', padding: '12px 20px', borderRadius: '10px',
          background: '#fffbf0', border: '1px solid #fde68a',
          fontSize: '13px', color: '#92400e', textAlign: 'center',
        }}>
          🧪 <strong>Beta Access</strong> — Exports are free during the testing phase. Thank you for being an early partner.
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
      `}</style>
    </>
  );
}
